<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import pMap from 'p-map';
import { useEditUser, useGetUsers } from '@ilteducation/customer-mgmt';

import Modal from '../Modal.vue';
import SidePanel from '../SidePanel/SidePanel.vue';
import { useLocale } from '../../composables/useLocale';
import SidePanelSection from '../SidePanel/SidePanelSection.vue';

import { useSelectedItems } from '../../composables/useSelectedItems';
import useModal, { modals } from '../..//composables/useModal';
import useToast from '../../composables/useToast';
import { useAppEvents } from '../../events/appEvents';
import { useCanDeleteUserAssociation } from '../../composables/useDeleteUserAssociation';

const { t } = useLocale();
const { toast } = useToast();
const { selectedItems, clearSelectedItems } = useSelectedItems();
const {
  isOpen: isConfirmDeletionModalOpen,
  open: openConfirmDeletionModal,
  close: closeConfirmDeletionModal,
} = useModal(modals.CONFIRM_DELETE_USERS);
const { deleteAssociation } = useEditUser();
const { emitAppEvent } = useAppEvents();

const props = defineProps<{
  totalUsers: number;
  organizationUid: string;
}>();
const emit = defineEmits(['close', 'users-deleted']);

const isDeleting = ref(false);

const deleteUser = async (uid: string) => {
  return deleteAssociation(uid, props.organizationUid);
};

const { fetchAllUsers } = useGetUsers();
const canDeleteUserAssociation = useCanDeleteUserAssociation();

const deletableByUid = new Map<string, boolean>();
const areSelectedUsersDeletable = ref(false);
watch(
  selectedItems,
  async (uids) => {
    areSelectedUsersDeletable.value = false;
    const unknownUids = uids.filter((uid) => !deletableByUid.has(uid));
    if (unknownUids.length > 0) {
      const users = await fetchAllUsers({ filters: { uids: unknownUids, schoolUnits: [props.organizationUid] } });
      const deletable = await Promise.all(users.map(canDeleteUserAssociation));
      users.forEach((user, index) => deletableByUid.set(user.uid, deletable[index]));
    }

    if (uids === selectedItems.value) {
      areSelectedUsersDeletable.value = uids.every((uid) => deletableByUid.get(uid) === true);
    }
  },
  { immediate: true },
);

const deleteSelectedUsers = async () => {
  isDeleting.value = true;
  await new Promise((resolve) => setTimeout(resolve, 2000));

  const results = await pMap(selectedItems.value, deleteUser, { concurrency: 5 });

  emitAppEvent({ type: 'association:updated', userUids: selectedItems.value });
  closeConfirmDeletionModal();
  if (results.every(Boolean)) {
    toast(t('lcp.users_deleted_count', { count: selectedItems.value.length }), 'success');
  } else {
    toast(t('lcp.error_deleting_users'), 'error');
  }
  emit('users-deleted', selectedItems.value);
  emit('close');
  isDeleting.value = false;
};

const onClose = () => {
  clearSelectedItems();
  emit('close');
};

const cancelDeleteUsers = () => {
  closeConfirmDeletionModal();
};

const title = computed(() => t(`lcp.users_selected_count`, { count: selectedItems.value.length }));
// TODO: enable for select all
// const subtitle = computed(() => t(`lcp.select_all_x_users`, { count: props.totalUsers }) + props.totalUsers);
</script>

<template>
  <!-- TODO: enable for select all
   :subtitle="subtitle"
  :subtitle-action="clearSelectedItems" -->
  <SidePanel :title="title" selected-entity="user" no-icon no-edit :on-close="onClose">
    <slot name="settings" :selectedUsers="selectedItems"></slot>
    <template #append>
      <SidePanelSection>
        <v-row class="justify-end" no-gutters>
          <v-col cols="auto">
            <v-btn
              color="error"
              prepend-icon="mdi-trash-can"
              :disabled="!areSelectedUsersDeletable"
              @click="openConfirmDeletionModal"
            >
              {{ t('lcp.delete_user') }}
            </v-btn>
          </v-col>
        </v-row>
      </SidePanelSection>
    </template>
  </SidePanel>
  <Modal
    :name="modals.CONFIRM_DELETE_USERS"
    :title="t('lcp.confirm_multiple_users_deletion')"
    :is-open="isConfirmDeletionModalOpen"
    :show-close-btn="true"
    :action="deleteSelectedUsers"
    action-color="error"
    :action-label="t('lcp.delete_users')"
    :cancel-action="cancelDeleteUsers"
    :cancel-action-label="t('lcp.back')"
    cancel-action-color="success"
    :loading="isDeleting"
  >
    <p>
      {{ t('lcp.confirm_multiple_users_deletion_message', { count: selectedItems.length }) }}
    </p>
  </Modal>
</template>

<style scoped lang="scss"></style>
